import { useTranslator } from '@bluebeam/react-i18n';
import LoadingAnimation from 'components/LoadingAnimation';
import { FeatureFlag, useFeatureFlags } from 'featureFlags';
import { useIntegrationsCheck } from 'hooks/useIntegrationsCheck';
import React, { FC, useEffect, useState } from 'react';
import { Redirect, RouteComponentProps, withRouter } from 'react-router-dom';
import { Project } from 'records/Project';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import ImportDrawingsDropdown from '../PublishedTab/Imports/ImportDrawingsDropdown';
import DragArea from './DragArea';

interface Props extends WithAppStoreProps, RouteComponentProps {}

const UploadFile: FC<Props> = ({ appState, actions, history, match }) => {
  const fileInputRef: React.RefObject<HTMLInputElement> = React.createRef();
  const { translate } = useTranslator(['uploadFile', 'toastText']);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [selectedProject, setSelectedProject] = useState('');
  const [isLoaded, setIsLoaded] = useState(false);
  const { studioProjects } = appState;
  const [currentProject, setCurrentProject] = useState(new Project());

  const {
    [FeatureFlag.ImportFromStudio]: isImportFromStudioEnabled,
    [FeatureFlag.ZipUploadsEnabled]: isZipUploadEnabled,
  } = useFeatureFlags();

  const canImportFromStudio =
    isImportFromStudioEnabled && studioProjects.size > 0;

  const { areIntegrationsEnabled } = useIntegrationsCheck();

  const accept = isZipUploadEnabled ? '.pdf,.zip' : '.pdf';

  const initialSetup = async () => {
    const currentProjectResponse = await appState.getProject();
    const toastText = {
      fileUploadError: {
        title: translate('toastText:fileUploadError.title'),
        message: translate('toastText:fileUploadError.message'),
      },
      drawingsReadyForReview: {
        title: translate('toastText:drawingsReadyForReview.title'),
        message: translate('toastText:drawingsReadyForReview.message'),
      },
      fileUploadSuccess: {
        title: translate('toastText:fileUploadSuccess.title'),
        message: translate('toastText:fileUploadSuccess.message'),
      },
    };

    setCurrentProject(currentProjectResponse);
    setIsLoaded(true);

    actions.getProcessingStatuses(toastText);
  };

  useEffect(() => {
    !isLoaded && initialSetup();

    return () => {
      setIsLoaded(false);
    };
  }, []);

  const handleUploadDrawings = (): void => {
    const fileInput = fileInputRef.current;

    if (fileInput) {
      fileInput.click();
    }
  };

  const handleFileSelect = async (): Promise<void> => {
    const fileInput = fileInputRef.current;

    if (fileInput && fileInput.files) {
      try {
        const files = [...Array.from(fileInput.files)];
        const toastText = {
          drawingsUploaded: {
            title: translate('toastText:drawingsUploaded.title'),
            message: translate('toastText:drawingsUploaded.message'),
          },
        };

        setUploadingDocs(true);
        actions.uploadDocuments(appState.selectedProject, files, toastText);
        history.push(`/project/${appState.selectedProject}/drawings/uploads`);

        window.setTimeout(actions.getProcessingStatuses, 1000);
      } catch (e) {
        setUploadingDocs(false);
      }
    }
  };

  const handleCancelClick = (): void => {
    history.push(`/project/${appState.selectedProject}/drawings`);
  };

  const handleDrop = (files: FileList): void => {
    history.push({
      pathname: `${match.url}/confirm`,
      state: { files },
    });
  };

  return selectedProject !== '' ? (
    <Redirect
      to={{
        pathname: `/project/${appState.selectedProject}/drawings/studio`,
        state: { selectedProject },
      }}
    />
  ) : (
    <>
      <div className="row items-center justify-between p4">
        <div className="font-size-5 medium mt1">
          {translate('uploadFile:selectUploadSource')}
        </div>
        <button
          className="btn-sm btn-link cursor-pointer"
          onClick={handleCancelClick}
        >
          {translate('buttons:cancel')}
        </button>
      </div>
      <div
        id="upload-drawing-tab"
        className="bgd-light-grey-2 bt-3 p4"
        style={{ height: 'calc(100% - 81px)' }}
      >
        <div
          className="row items-center justify-center w100 h100 b-dashed b-primary"
          style={{ borderWidth: '2px' }}
        >
          {isLoaded ? (
            <DragArea handleDrop={handleDrop}>
              <div className="column center items-center justify-center h100">
                <div className="black-2 medium font-size-5">
                  {translate('uploadFile:dragAndDrop')}
                </div>
                <div className="black-1 light font-size-4 py5">
                  {translate('uploadFile:or')}
                </div>
                <div
                  className="column justify-center"
                  style={{ width: '184px' }}
                >
                  <div className="w100">
                    <button
                      id="upload-drawings-upload-btn"
                      className="btn-lg btn-primary w100"
                      onClick={handleUploadDrawings}
                      disabled={
                        uploadingDocs ||
                        !currentProject.permissions.activeProject
                      }
                    >
                      {translate('buttons:browseFiles')}
                    </button>
                    <input
                      id="upload-drawings-upload-input"
                      type="file"
                      accept={accept}
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      multiple
                      hidden
                    />
                  </div>
                  {(areIntegrationsEnabled || canImportFromStudio) && (
                    <div className="mt4 w100">
                      <ImportDrawingsDropdown />
                    </div>
                  )}
                </div>
              </div>
            </DragArea>
          ) : (
            <div className="flex-center h100">
              <div className="column items-center">
                <LoadingAnimation pageName="Loading" />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default withRouter(withAppStore(UploadFile));
